const S3_BUCKET_ORIGIN = 'https://raw-website-20260413153456.s3.us-east-1.amazonaws.com';
const CONTENT_PREFIX = 'content/';
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];

let contentPromise;

function numberFromText(value) {
  const match = String(value || '').match(/(\d+)/);
  return match ? Number(match[1]) : null;
}

function naturalCompare(a, b) {
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function numberAwareCompare(a, b) {
  const aNum = numberFromText(a);
  const bNum = numberFromText(b);

  if (aNum !== null && bNum !== null && aNum !== bNum) return aNum - bNum;
  if (aNum !== null && bNum === null) return -1;
  if (aNum === null && bNum !== null) return 1;
  return naturalCompare(a, b);
}

function isImageKey(key) {
  const lower = key.toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

function keyToPublicUrl(key) {
  return `/${key.split('/').map((segment) => encodeURIComponent(segment)).join('/')}`;
}

function keyToS3Url(key) {
  return `${S3_BUCKET_ORIGIN}/${key.split('/').map((segment) => encodeURIComponent(segment)).join('/')}`;
}

function decodeXmlEntities(value) {
  return value
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function parseTagValues(xmlText, tagName) {
  const regex = new RegExp(`<${tagName}>([\\s\\S]*?)<\\/${tagName}>`, 'g');
  const values = [];
  let match = regex.exec(xmlText);
  while (match) {
    values.push(decodeXmlEntities(match[1]));
    match = regex.exec(xmlText);
  }
  return values;
}

function parseS3ListResponse(xmlText) {
  const errorCode = parseTagValues(xmlText, 'Code')[0];
  if (errorCode) {
    throw new Error(`S3 listing failed: ${errorCode}`);
  }

  const keys = parseTagValues(xmlText, 'Key');
  const prefixes = parseTagValues(xmlText, 'Prefix');
  const commonPrefixes = prefixes.filter((prefix) => prefix !== CONTENT_PREFIX);
  const isTruncated = parseTagValues(xmlText, 'IsTruncated')[0] === 'true';
  const nextToken = parseTagValues(xmlText, 'NextContinuationToken')[0] || '';

  return {
    keys,
    commonPrefixes,
    isTruncated,
    nextToken
  };
}

async function fetchText(url) {
  const response = await fetch(url, { cache: 'no-cache' });
  if (!response.ok) {
    throw new Error(`Request failed (${response.status}) for ${url}`);
  }
  return response.text();
}

async function fetchJsonByKey(key) {
  const response = await fetch(keyToS3Url(key), { cache: 'no-cache' });
  if (!response.ok) return null;
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

async function fetchTextByKey(key) {
  const response = await fetch(keyToS3Url(key), { cache: 'no-cache' });
  if (!response.ok) return '';
  return response.text();
}

async function listS3({ prefix, delimiter = '' }) {
  const allKeys = [];
  const allCommonPrefixes = [];
  let token = '';
  let pageCount = 0;

  while (pageCount < 20) {
    const params = new URLSearchParams();
    params.set('list-type', '2');
    params.set('prefix', prefix);
    if (delimiter) params.set('delimiter', delimiter);
    if (token) params.set('continuation-token', token);

    const xmlText = await fetchText(`${S3_BUCKET_ORIGIN}/?${params.toString()}`);
    const parsed = parseS3ListResponse(xmlText);

    allKeys.push(...parsed.keys);
    allCommonPrefixes.push(...parsed.commonPrefixes);

    if (!parsed.isTruncated || !parsed.nextToken) break;
    token = parsed.nextToken;
    pageCount += 1;
  }

  return {
    keys: [...new Set(allKeys)],
    commonPrefixes: [...new Set(allCommonPrefixes)]
  };
}

function folderNameFromPrefix(prefix, basePrefix) {
  return prefix.replace(basePrefix, '').replace(/\/$/, '');
}

function pickFirstMatchingImageKey(keys, preferredNames) {
  const preferredLower = preferredNames.map((value) => value.toLowerCase());
  const preferred = keys.find((key) => preferredLower.includes(key.split('/').pop().toLowerCase()));
  if (preferred) return preferred;
  return keys.find(isImageKey) || '';
}

function sortKeysByFileName(keys) {
  return [...keys].sort((a, b) => {
    const aName = decodeURIComponent(a.split('/').pop() || '');
    const bName = decodeURIComponent(b.split('/').pop() || '');
    const byNumber = numberAwareCompare(aName, bName);
    if (byNumber !== 0) return byNumber;
    return naturalCompare(a, b);
  });
}

async function buildProjects() {
  const basePrefix = `${CONTENT_PREFIX}Works/Projects/`;

  // Single flat listing fetches all project keys in one S3 call instead of one per project folder
  const allObjects = await listS3({ prefix: basePrefix });

  // Group keys by top-level project folder
  const projectFolderMap = new Map();
  for (const key of allObjects.keys) {
    const relative = key.slice(basePrefix.length);
    const firstSlash = relative.indexOf('/');
    if (firstSlash === -1) continue;
    const folder = relative.slice(0, firstSlash);
    if (!projectFolderMap.has(folder)) projectFolderMap.set(folder, []);
    projectFolderMap.get(folder).push(key);
  }

  const folders = [...projectFolderMap.keys()].sort(numberAwareCompare);

  // Only details.json fetches remain (image keys are already known)
  const entries = await Promise.all(
    folders.map(async (folder) => {
      const detailsKey = `${basePrefix}${folder}/Details/details.json`;
      const details = await fetchJsonByKey(detailsKey);
      if (!details) return null;

      const imageKeys = (projectFolderMap.get(folder) || []).filter((key) => isImageKey(key));

      const thumbnailCandidates = imageKeys.filter((key) => key.toLowerCase().includes('/thumbnail/'));
      const thumbnailKey =
        pickFirstMatchingImageKey(thumbnailCandidates, ['thumbnail.jpg', 'thumbnail.jpeg', 'thumbnail.png']) || '';

      const galleryKeys = sortKeysByFileName(
        imageKeys.filter((key) => {
          const lk = key.toLowerCase();
          return !lk.includes('/thumbnail/') && !lk.includes('/details/') && !/thumb/i.test(key.split('/').pop());
        })
      );

      return {
        slug: folder,
        name: details.id || details.title || folder,
        title: details.title || details.id || folder,
        client: details.client || '',
        location: details.location || '',
        status: details.status || '',
        description: details.description || '',
        projectTimeline: details.projectTimeline || '',
        priority: Number(details.priority) || 0,
        thumbnailUrl: thumbnailKey ? keyToPublicUrl(thumbnailKey) : '',
        gallery: galleryKeys.map(keyToPublicUrl)
      };
    })
  );

  return entries.filter(Boolean).sort((a, b) => {
    const byFolder = numberAwareCompare(a.slug, b.slug);
    if (byFolder !== 0) return byFolder;
    return (b.priority || 0) - (a.priority || 0);
  });
}

async function buildSimpleSection(pathParts, preferredImages = ['image.jpg', 'image.jpeg', 'image.png']) {
  const basePrefix = `${CONTENT_PREFIX}${pathParts.join('/')}/`;
  const folderResponse = await listS3({ prefix: basePrefix, delimiter: '/' });
  const folders = folderResponse.commonPrefixes
    .map((prefix) => folderNameFromPrefix(prefix, basePrefix))
    .sort(numberAwareCompare);

  const entries = await Promise.all(
    folders.map(async (folder) => {
      const folderPrefix = `${basePrefix}${folder}/`;
      const details = await fetchJsonByKey(`${folderPrefix}details.json`);
      if (!details) return null;

      const objectResponse = await listS3({ prefix: folderPrefix });
      const imageKey = pickFirstMatchingImageKey(
        objectResponse.keys.filter((key) => isImageKey(key)),
        preferredImages
      );

      return {
        slug: folder,
        title: details.title || folder,
        description: details.description || '',
        url: details.url || '',
        priority: Number(details.priority) || 0,
        tel: details.tel || '',
        mail: details.mail || '',
        imageUrl: imageKey ? keyToPublicUrl(imageKey) : ''
      };
    })
  );

  return entries.filter(Boolean).sort((a, b) => {
    const byFolder = numberAwareCompare(a.slug, b.slug);
    if (byFolder !== 0) return byFolder;
    return (b.priority || 0) - (a.priority || 0);
  });
}

async function buildPositions() {
  const basePrefix = `${CONTENT_PREFIX}About/About Us/Five Positions/`;
  const folderResponse = await listS3({ prefix: basePrefix, delimiter: '/' });
  const folders = folderResponse.commonPrefixes
    .map((prefix) => folderNameFromPrefix(prefix, basePrefix))
    .sort(numberAwareCompare);

  const entries = await Promise.all(
    folders.map(async (folder) => {
      const folderPrefix = `${basePrefix}${folder}/`;
      const details = await fetchJsonByKey(`${folderPrefix}details.json`);
      if (!details) return null;

      const objectResponse = await listS3({ prefix: folderPrefix });
      const keys = objectResponse.keys;
      const imageKey = pickFirstMatchingImageKey(keys.filter((key) => isImageKey(key)), ['image.jpg', 'image.jpeg']);
      const thumbnailKey = pickFirstMatchingImageKey(
        keys.filter((key) => isImageKey(key)),
        ['thumbnail.jpg', 'thumbnail.jpeg', 'thumbnail.png']
      );

      return {
        key: folder.replace(/^\d+-/, '').trim().toLowerCase(),
        slug: folder,
        title: details.title || folder,
        thumbnail: details.thumbnail || details.title || folder,
        description: details.description || '',
        imageUrl: imageKey ? keyToPublicUrl(imageKey) : '',
        thumbnailUrl: thumbnailKey ? keyToPublicUrl(thumbnailKey) : ''
      };
    })
  );

  return entries.filter(Boolean).sort((a, b) => numberAwareCompare(a.slug, b.slug));
}

async function buildHome() {
  const homePrefix = `${CONTENT_PREFIX}Home/`;
  const homeRoot = await listS3({ prefix: homePrefix, delimiter: '/' });
  const rootImageKeys = sortKeysByFileName(
    homeRoot.keys.filter((key) => key !== homePrefix && !key.includes('/Logo/') && isImageKey(key))
  );

  const logoObjects = await listS3({ prefix: `${homePrefix}Logo/` });
  const logoKey = pickFirstMatchingImageKey(logoObjects.keys, ['Title 1.png', 'Title.png', 'Title 2.png']);

  return {
    images: rootImageKeys.map(keyToPublicUrl),
    logoUrl: logoKey ? keyToPublicUrl(logoKey) : ''
  };
}

async function buildAbout() {
  const coverKey = `${CONTENT_PREFIX}About/About Us/about-us-cover.jpg`;
  const response = await fetch(keyToS3Url(coverKey), { method: 'HEAD', cache: 'no-cache' });
  return {
    coverImageUrl: response.ok ? keyToPublicUrl(coverKey) : ''
  };
}

async function buildContact() {
  const text = await fetchTextByKey(`${CONTENT_PREFIX}Contact/contact Information.txt`);
  const email = (text.match(/Email:\s*(.*)/i) || [])[1] || '';
  const address = (text.match(/Address:\s*(.*)/i) || [])[1] || '';
  const mobile = (text.match(/Mobile\s*Num:\s*(.*)/i) || [])[1] || '';

  return {
    email: email.trim(),
    address: address.trim(),
    mobileNumbers: mobile
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
  };
}

async function loadDynamicContentFromS3() {
  const [home, about, contact, projects, awards, publications, talks, team, positions] = await Promise.all([
    buildHome(),
    buildAbout(),
    buildContact(),
    buildProjects(),
    buildSimpleSection(['About', 'Awards_Recognitions']),
    buildSimpleSection(['About', 'Publications']),
    buildSimpleSection(['About', 'Talks']),
    buildSimpleSection(['About', 'team']),
    buildPositions()
  ]);

  return {
    generatedAt: new Date().toISOString(),
    home,
    about,
    contact,
    projects,
    awards,
    publications,
    talks,
    team,
    positions
  };
}

async function loadManifestFallback() {
  const response = await fetch('/content/content-manifest.json', { cache: 'no-cache' });
  if (!response.ok) {
    throw new Error('Unable to load content data from S3 or manifest.');
  }
  return response.json();
}

// Per-section lazy caches — each page only triggers the sections it needs
const sectionCache = {};

function loadSection(key, buildFn, fallbackFn) {
  if (!sectionCache[key]) {
    sectionCache[key] = buildFn()
      .catch(() => (fallbackFn ? fallbackFn() : Promise.reject(new Error(`Failed to load ${key}`))))
      .catch((error) => {
        delete sectionCache[key];
        throw error;
      });
  }
  return sectionCache[key];
}

async function getManifestSection(key, defaultValue) {
  const manifest = await loadManifestFallback();
  return manifest[key] || defaultValue;
}

export async function getHomeContent() {
  return loadSection('home', buildHome, () => getManifestSection('home', { images: [], logoUrl: '' }));
}

export async function getAboutContent() {
  return loadSection('about', buildAbout, () => getManifestSection('about', { coverImageUrl: '' }));
}

export async function getContactContent() {
  return loadSection('contact', buildContact, () =>
    getManifestSection('contact', { email: '', address: '', mobileNumbers: [] })
  );
}

export async function getProjects() {
  return loadSection('projects', buildProjects, () => getManifestSection('projects', []));
}

export async function getProjectBySlug(slug) {
  const projects = await getProjects();
  return projects.find((project) => project.slug === slug) || null;
}

export async function getAwards() {
  return loadSection('awards', () => buildSimpleSection(['About', 'Awards_Recognitions']), () =>
    getManifestSection('awards', [])
  );
}

export async function getPublications() {
  return loadSection('publications', () => buildSimpleSection(['About', 'Publications']), () =>
    getManifestSection('publications', [])
  );
}

export async function getTalks() {
  return loadSection('talks', () => buildSimpleSection(['About', 'Talks']), () =>
    getManifestSection('talks', [])
  );
}

export async function getTeamMembers() {
  return loadSection('team', () => buildSimpleSection(['About', 'team']), () =>
    getManifestSection('team', [])
  );
}

export async function getPositions() {
  return loadSection('positions', buildPositions, () => getManifestSection('positions', []));
}

export async function getPositionByKey(key) {
  const positions = await getPositions();
  return positions.find((position) => position.key === key) || null;
}
